import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { FaSearch, FaRegStar } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import shop from "../../../api/shop";
import "./NavbarSearch.css";

const NavbarSearch = ({ isMobile = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const history = useHistory();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Debounced search query
  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setSuggestions([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setIsOpen(true);

    const handler = setTimeout(() => {
      shop.getProducts(
        (res) => {
          setSuggestions(res.data || []);
          setLoading(false);
        },
        null, // categoryId = null (search all categories)
        null,
        () => {},
        1,
        trimmed
      );
    }, 280);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Navigate to full products page with search query
  const handleFullSearch = (e) => {
    if (e) e.preventDefault();
    const trimmed = searchTerm.trim();
    setIsOpen(false);
    setIsFocused(false);
    if (inputRef.current) inputRef.current.blur();

    if (trimmed) {
      history.push({
        pathname: `${process.env.PUBLIC_URL}/products/Products-all`,
        search: `?search=${encodeURIComponent(trimmed)}&page=1`,
      });
    } else {
      history.push(`${process.env.PUBLIC_URL}/products/Products-all`);
    }
  };

  // Navigate to selected product details
  const handleSelectProduct = (item) => {
    setIsOpen(false);
    setIsFocused(false);
    setSearchTerm("");
    if (inputRef.current) inputRef.current.blur();

    const slug = item.api?.url_key
      ? item.api.url_key
      : (item.name || "product").toLowerCase().replace(/\s+/g, "-");
    const targetUrl = `${process.env.PUBLIC_URL}/product/${slug}-${item.id}`;
    history.push({
      pathname: targetUrl,
      state: {
        category_ids:
          item.category?.[0]?.category_id ||
          item.category?.[0]?.id ||
          item.api?.Category?.[0]?.id ||
          null,
        product_id: item.id,
      },
    });
  };

  // Clear search input
  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm("");
    setSuggestions([]);
    setIsOpen(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const wrapClass = isMobile
    ? "nav-search-mobile-wrap"
    : "nav-search-desktop-wrap";
  const boxClass = isMobile
    ? `nav-search-mobile-box ${isFocused ? "focused" : ""}`
    : `nav-search-desktop-box ${isFocused ? "focused" : ""}`;

  return (
    <div className={wrapClass} ref={wrapperRef}>
      <form onSubmit={handleFullSearch} className={boxClass} role="search">
        <button
          type="button"
          onClick={handleFullSearch}
          className="nav-search-icon-btn"
          aria-label="Submit search"
          style={{
            marginRight: isMobile ? "8px" : "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <FaSearch style={{ fontSize: isMobile ? "14px" : "13px" }} />
        </button>

        <input
          ref={inputRef}
          type="text"
          className="nav-search-input"
          style={{
            background: "transparent",
            backgroundColor: "transparent",
            border: "none",
            borderWidth: 0,
            outline: "none",
            boxShadow: "none",
            WebkitBoxShadow: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
            padding: "0 0 0 6px",
            margin: 0,
            color: "#FCFEFD",
            fontSize: isMobile ? "13.5px" : "13px",
            lineHeight: "normal",
            width: "100%",
            letterSpacing: "0.25px",
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            if (searchTerm.trim().length > 0) {
              setIsOpen(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleFullSearch(e);
            } else if (e.key === "Escape") {
              setIsOpen(false);
              setIsFocused(false);
              if (inputRef.current) inputRef.current.blur();
            }
          }}
          placeholder="Search..."
          aria-label="Search products"
          autoComplete="off"
        />

        {searchTerm.length > 0 && (
          <button
            type="button"
            className="nav-search-clear-btn"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <MdClose />
          </button>
        )}
      </form>

      {/* Dropdown Live Results */}
      {isOpen && searchTerm.trim().length > 0 && (
        <div className="nav-search-dropdown">
          {loading ? (
            <div className="nav-search-status-row">
              <span className="nav-search-spinner" />
              <span>Searching products...</span>
            </div>
          ) : suggestions && suggestions.length > 0 ? (
            <>
              <ul className="nav-search-list">
                {suggestions.map((item, index) => {
                  const ratingAvg = item.api?.reviews?.average_rating || "0.0";
                  const ratingTotal = item.api?.reviews?.total || "0";
                  const imgUrl =
                    item.pictures ||
                    (item.images && item.images[0]?.url) ||
                    "";

                  return (
                    <li
                      key={item.id || index}
                      className="nav-search-item"
                      onClick={() => handleSelectProduct(item)}
                    >
                      <div className="nav-search-thumb-wrap">
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={item.name}
                            className="nav-search-thumb-img"
                          />
                        ) : null}
                      </div>

                      <div className="nav-search-info">
                        <h5 className="nav-search-product-name">{item.name}</h5>
                        <div className="nav-search-meta-row">
                          <span className="nav-search-price">
                            {item.price || item.formated_price}
                          </span>
                          <div className="nav-search-rating">
                            <FaRegStar className="nav-search-rating-star" />
                            <span>
                              ({ratingAvg})({ratingTotal})
                            </span>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div
                className="nav-search-view-all"
                onClick={handleFullSearch}
                role="button"
                tabIndex={0}
              >
                <span>View all results for &ldquo;{searchTerm}&rdquo;</span>
                <span aria-hidden="true">&rarr;</span>
              </div>
            </>
          ) : (
            <div className="nav-search-status-row">
              <span>No products found for &ldquo;{searchTerm}&rdquo;</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NavbarSearch;
